import { useContext, useEffect, useRef, useState } from 'react';
import AppContext from '../contexts/AppContext';
import DataContext from '../contexts/DataContext';
import Button from './Button';
import iconSolarHouse from '../images/solar-house-circle.png';
import solarHomeMarker from '../images/map-marker.png';
import leadsZipData from '../data/leads_zip_data.json';
import { isMobile } from './../helpers';

declare const google: any;
const MIN_HOUSEHOLDS_WITH_SOLAR = 3;

function homesWithSolarForZip(zip: string) {
  return (
    leadsZipData[zip as keyof typeof leadsZipData] ?? MIN_HOUSEHOLDS_WITH_SOLAR
  );
}

export default function NeighborsMap() {
  const { googleAPILoaded, goToNextSlide } = useContext(AppContext);
  const { data, updateData } = useContext(DataContext);
  const mapRef = useRef(null);
  const [address, setAddress] = useState<string>('');

  let homesWithSolar = homesWithSolarForZip(data.selectedZipCode);
  if (homesWithSolar < MIN_HOUSEHOLDS_WITH_SOLAR) {
    homesWithSolar = MIN_HOUSEHOLDS_WITH_SOLAR;
  }
  const formattedHomesWithSolar = homesWithSolar.toLocaleString('en-US');

  const initGoogleMap = async () => {
    if (!googleAPILoaded) return;

    const { Map } = await google.maps.importLibrary('maps');
    const map = new Map(mapRef.current as any, {
      zoom: 15,
      mapTypeId: 'satellite',
      disableDoubleClickZoom: true,
      draggable: false,
      scrollwheel: false,
      fullscreenControl: false,
      mapTypeControl: false,
      rotateControl: false,
      streetViewControl: false,
      zoomControl: false,
    });

    const request = {
      query: address,
      fields: ['geometry'],
    };

    const updateMap = (results: any) => {
      const latLng = {
        lat: results[0].geometry.location.lat(),
        lng: results[0].geometry.location.lng(),
      };
      map.setCenter(latLng);

      new google.maps.Marker({
        map,
        position: latLng,
        title: address,
      });
    };

    const service = new google.maps.places.PlacesService(map);
    service.findPlaceFromQuery(request, (results: any, status: any) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && results) {
        updateMap(results);
      } else {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
          { address },
          async function (results: any, status: any) {
            if (status === 'OK') {
              updateMap(results);
            }
          }
        );
      }
    });

    const boundsListener = google.maps.event.addListener(
      map,
      'bounds_changed',
      function () {
        if (map.getBounds()) {
          const topRightLat = map.getBounds()?.getNorthEast().lat();
          const topRightLng = map.getBounds()?.getNorthEast().lng();
          const bottomLeftLat = map.getBounds()?.getSouthWest().lat();
          const bottomLeftLng = map.getBounds()?.getSouthWest().lng();

          if (topRightLat && topRightLng && bottomLeftLat && bottomLeftLng) {
            const difLat = Math.abs(topRightLat - bottomLeftLat);
            const difLng = Math.abs(topRightLng - bottomLeftLng);

            const homesToDisplay =
              homesWithSolar < MIN_HOUSEHOLDS_WITH_SOLAR
                ? 3
                : homesWithSolar >= 100
                ? 100
                : homesWithSolar;

            for (let i = 0; i < homesToDisplay; i++) {
              const markerLat = bottomLeftLat + difLat * Math.random();
              const markerLng = bottomLeftLng + difLng * Math.random();

              new google.maps.Marker({
                position: {
                  lat: markerLat,
                  lng: markerLng,
                },
                map,
                icon: solarHomeMarker,
              });
            }
          }
          google.maps.event.removeListener(boundsListener);
        }
      }
    );
  };

  useEffect(() => {
    setAddress(data.googleFormattedAddress);
  }, []);

  useEffect(() => {
    initGoogleMap();
  }, [googleAPILoaded, address]);

  return (
    <div className={isMobile() ? 'max-w-[350px]' : ''}>
      {/* <p className="mt-[30px] md:mt-10 mb-4 text-[16px] short:!text-[16px] md:text-[18px]">
        <span className="font-bold underline">{formattedHomesWithSolar}</span>{' '}
        households in your neighborhood are going solar with average savings of{' '}
        <span className="font-bold underline">$69,215</span>. Continue on to see
        how much you can save.
      </p>
      <p className="mt-[30px] md:mt-6 mb-4 text-[16px] short:!text-[16px] md:text-[18px]">
        Your address:&nbsp; {data.googleFormattedAddress}
      </p> */}
      <div
        ref={mapRef}
        className={`h-[245px] lg:h-[350px] w-full rounded-[12px] md:rounded-[24px] mt-[30px] md:mt-10`}></div>
      <div className="flex my-8">
        <div className="mr-3">
          <img
            alt="Home with solar panel icon"
            className="md:w-[50px] h-auto"
            src={iconSolarHouse}
          />
        </div>
        <div className="flex flex-col">
          <div className="text-[16px] short:!text-[16px] md:text-[18px]">
            {formattedHomesWithSolar} households in your area expressed interest
            in a solar system last year.
          </div>
          <div className="text-[14px] text-enph-grey-02">
            The average bill savings over 25 years is $69,215.
          </div>
        </div>
      </div>
      <Button
        title="Continue"
        action={() => {
          goToNextSlide();
        }}
      />
    </div>
  );
}
